import React, { useContext, useEffect, useState } from 'react';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { Alert, Grid } from '@mui/material';
import { useParams } from 'react-router-dom';
import axios from '@/_utils/axios';
import { insertWordBreaks } from '@/_utils/utils';
import { FileTypes } from '@/constants/constants';
import { ConfigContext } from '@/contextAPI/configContext';
import { selectPreviewFile } from '@/redux/selectors/orderPlacementSelectors';
import { selectTitlePoster, selectActiveTitle } from '@/redux/selectors/titleSelectors';
import { selectActiveTitle as selectActiveTitleV2, selectActiveTitlePoster } from '@/redux/selectors/titleV2Selectors';
import { setPreviewFile } from '@/redux/slices/orderPlacementSlice';
import Modal from '@/components/_common/Modal/Modal';
import { selectFileTypes } from '@/redux/selectors/constantsSelectors';
import 'react18-json-view/src/style.css';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { InventoryFileEntity } from '@/redux/slices/slice_types';
import FileInventoryTags from './FileInventoryTags';
import InventoryPreview from '@/components/_common/InventoryPreview/InventoryPreview';

const InventoryPreviewModal = () => {
  const dispatch = useAppDispatch();
  const isTitleV2 = useContext(ConfigContext)?.titleConfig?.v2;
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [source, setSource] = useState<string | null>(null);
  const [error, setError] = useState<string | null | boolean>(null);
  const activeTitle = useAppSelector(isTitleV2 ? selectActiveTitleV2 : selectActiveTitle);
  const previewFile = useAppSelector(selectPreviewFile);
  const poster = useAppSelector(isTitleV2 ? selectActiveTitlePoster : selectTitlePoster) ?? undefined;
  const file_type = previewFile?.file_type;
  const [modalOpen, setModalOpen] = useState(false);
  const fileTypes = useAppSelector(selectFileTypes);
  const rootTitleId = id ? id : activeTitle?.lineage ? activeTitle?.lineage[activeTitle?.lineage?.length - 1] : activeTitle?.id;

  const handleError = () => {
    setError('There was an error retrieving the file.');
  };

  const fetchSource = (previewFileArg: InventoryFileEntity) => {
    setLoading(true);
    setError(false);

    const fileTypeGrouping = fileTypes[file_type as keyof FileTypes];
    if (
      (fileTypeGrouping === 'image' || fileTypeGrouping === 'video' || fileTypeGrouping === 'audio') &&
      previewFileArg?.is_rally_inventory === false &&
      (previewFileArg?.non_rally_inventory_download_url || '').length > 0
    ) {
      setSource(previewFileArg?.non_rally_inventory_download_url);
      setLoading(false);
      return;
    }

    axios
      .get(`/inventory/titles/${rootTitleId}/files/${previewFileArg.id}/`)
      .then((res) => {
        if (!res?.data?.success) throw new Error();
        else {
          setSource(res.data?.data?.url);
        }
      })
      .catch(() => setError('There was a problem retrieving the file.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (previewFile) {
      setModalOpen(true);
      fetchSource(previewFile);
    } else {
      setModalOpen(false);
      setSource(null);
    }
  }, [previewFile, rootTitleId]);

  const onClose = () => {
    dispatch(setPreviewFile(null));
  };

  return (
    <Modal
      open={modalOpen}
      onClose={onClose}
      loading={loading}
      maxWidth={1300}
      icon={<PolarisIcon icon="preview" />}
      title={
        <>
          <strong>Preview:</strong> {previewFile ? insertWordBreaks(previewFile.name) : ''}
        </>
      }
    >
      {error && (
        <Grid container justifyContent="center" sx={{ mt: 8 }}>
          <Alert severity="error" sx={{ display: 'inline-flex' }}>
            {error}
          </Alert>
        </Grid>
      )}

      {!error && file_type && previewFile && source && (
        <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: 100 }}>
          <Grid container flexDirection="column">
            <FileInventoryTags file={previewFile} style={{ marginBottom: 10 }} />
            <InventoryPreview fileType={file_type} source={source} poster={poster} onError={handleError} />
          </Grid>
        </Grid>
      )}
    </Modal>
  );
};

export default InventoryPreviewModal;
